import { useState, useEffect } from 'react';
import { getsupport } from '../components/DbServices';
import Layout from '../layout/Layout';
import PageTransition from '../components/PageTransition';
import { getStoredUser } from '../utilities/encryption';
import Swal from 'sweetalert2';

export default function SupportList() {
  const user = getStoredUser();
  const userId = user?.data?.id;
  
  const [support, setSupport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const fetchSupportTickets = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await getsupport();
      // Ensure we are setting an array
      setSupport(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch Data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupportTickets();
  }, [userId]);

  // Search logic filtered by Name or Email
  const filteredSupport = support.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentItems = filteredSupport.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredSupport.length / itemsPerPage);

  return (
    <Layout>
      <PageTransition>
        <div className="container-fluid py-4">
          
          {/* HEADER */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
              <h3 className="fw-black mb-1 text-white">Support <span className="text-primary">Tickets</span></h3>
              <p className="text-white-50 small fw-medium m-0">Manage incoming user inquiries and technical signals.</p>
            </div>
            {/* Search Bar */}
            <div className="d-flex gap-2">
              <input 
                type="text" 
                className="form-control form-control-sm bg-dark border-secondary text-white rounded-pill px-3"
                placeholder="Search signals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '250px' }}
              />
            </div>
          </div>

          {/* TABLE CONTAINER */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr className="text-muted small text-uppercase fw-bold">
                    <th className="ps-4 py-4">Date</th>
                    <th className="py-4">Email</th>
                    <th className="py-4 text-center">Name</th>
                    <th className="py-4">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="4" className="text-center py-5"><div className="spinner-border text-primary border-2"></div></td></tr>
                  ) : currentItems.length === 0 ? (
                    <tr><td colSpan="4" className="text-center py-5 text-muted">No support tickets found in the database.</td></tr>
                  ) : (
                    currentItems.map(s => (
                      <tr key={s.id} className="border-bottom border-light">
                        {/* DATE COLUMN */}
                        <td className="ps-4 py-3">
                          <div className="text-dark small fw-bold">
                            <i className="bi bi-calendar3 me-2 text-primary"></i>
                            {new Date(s.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                          </div>
                        </td>
                        
                        {/* EMAIL COLUMN */}
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center" style={{width: 32, height: 32}}>
                                <i className="bi bi-envelope-fill small"></i>
                            </div>
                            <span className="text-dark fw-medium small">{s.email}</span>
                          </div>
                        </td>

                        {/* NAME COLUMN */}
                        <td className="text-center">
                          <span className="badge bg-light text-dark border fw-bold px-3 py-2" style={{ fontSize: '11px' }}>
                            {s.name?.toUpperCase()}
                          </span>
                        </td>

                        {/* DESCRIPTION COLUMN */}
                        <td>
                          <div className="text-muted small text-truncate" style={{ maxWidth: '300px' }} title={s.description}>
                            {s.description}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* PAGINATION */}
          {!loading && totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4 px-2">
              <p className="text-white-50 small mb-0">Total Signals: <strong>{filteredSupport.length}</strong></p>
              <nav>
                <ul className="pagination pagination-sm mb-0 gap-1">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link border-0 rounded-pill px-3 shadow-sm bg-dark text-white" onClick={() => setCurrentPage(prev => prev - 1)}>Previous</button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                      <button 
                        className={`page-link border-0 rounded-pill px-3 mx-1 ${currentPage === i + 1 ? 'bg-primary text-white' : 'bg-dark text-white'}`} 
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link border-0 rounded-pill px-3 shadow-sm bg-dark text-white" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </PageTransition>
    </Layout>
  );
}